/**
 * Per-scenario workflow definitions.  A scenario listed here overrides the
 * step-panel view with a true interactive fake-phone workflow (DemoWorkflow).
 * Scenarios NOT listed here continue to use the existing panel-based demo.
 *
 * Rules
 *  - All data is fictional.
 *  - Every workflow lives in an isolated, frontend-only state store.
 *  - No fetches, no writes, no timers other than the chat animation.
 */

import customWorkflows from "./custom";

// Shared scene-setter photos.  Real photos anchor each demo before the
// system UI appears so the promoter walks the manager through the moment
// the enquiry arrives at the shop, not just a wall of chat text.
const SCENE = {
  // Employee at her office desk quickly checking an incoming customer
  // message: used for every text-based intake (Messenger / WhatsApp / SMS).
  chatIntake: "https://customer-assets-wrfwihn1.emergentagent.net/job_9c33b626-e523-4641-99a7-61794eb720c3/artifacts/dqwu7nrr_employee_working_in_the_office_text_message.png",
  // Employee at her desk on the phone with a customer.
  phoneCall:  "https://customer-assets-wrfwihn1.emergentagent.net/job_9c33b626-e523-4641-99a7-61794eb720c3/artifacts/zkc8gub3_employee_working_in_the_office_calling_on_phone.png",
  // Actual damage photo attached to the incident/warning workflow.
  damagedCar: "https://customer-assets-wrfwihn1.emergentagent.net/job_9c33b626-e523-4641-99a7-61794eb720c3/artifacts/a59gh4xo_damaged_car.png",
};

// One-frame scene-setter for text-based intake channels.  Kept short on
// purpose: the real scenario is the chat, this just grounds it.
const chatIntakeIntro = (channel, business) => ([
  {
    img: SCENE.chatIntake,
    alt: `${business}: staff sees a new ${channel} message`,
    caption: `${business}. Gizelyn is at her desk when a new ${channel} message from a customer pings on her phone.`,
  },
]);

// Priority: booking intake channels (chat → notepad → draft → dashboard → confirm).
// Shared per-outcome recaps for the text/voice/walk-in intake channels.
// Related IDs are lookups into SCENARIOS (see scenarios.js) so the summary
// card can hyperlink to whatever adjacent flow makes narrative sense.
const INTAKE_SUMMARIES = {
  confirmed: {
    title: "Customer confirmed: booking is locked in",
    what:  "The message became a Notepad note, then a booking draft, then a real booking. The customer confirmed the details through the confirmation link, so no back-and-forth chat was needed.",
    related: ["upcoming-pickup", "deposit-balance"],
    relatedIntro: "Next you'd usually get ready for the pickup or record the deposit.",
  },
  requested_change: {
    title: "Customer asked for a change",
    what:  "Instead of confirming, the customer replied through the link asking for a different date, vehicle, or price. The booking stays in ConfirmPH so you can update it and resend.",
    related: ["date-change", "double-booking"],
    relatedIntro: "See what happens when you actually change the dates on this booking.",
  },
  declined: {
    title: "Customer declined the booking",
    what:  "The customer replied 'no thanks' through the confirmation link. The booking is not lost: it stays as a declined record so you have the history and can follow up later.",
    related: ["forgotten-booking", "follow-up"],
    relatedIntro: "Recording declines matters: it's how you spot patterns and stop losing leads.",
  },
  drafted: {
    title: "Booking recorded from the message",
    what:  "The inquiry became a structured booking record with the customer, vehicle, and dates together. Now anyone on the team can pick it up without re-reading the chat.",
    related: ["awaiting-confirmation", "double-booking"],
  },
};

const INTAKE = ({ id, business, channel, customer, vehicle, plate,
                    dates, pickupLocation, price, deposit, balance, notes }) => ({
  id,
  kind: "intake",
  business,
  home: ["channel", "notepad", "dashboard"],
  entry: "channel",
  channel: { ...channel, customer, business, vehicle },
  notepad: {
    placeholder: {
      customer, contact: channel.kind, vehicle,
      pickup: dates.pickup, pickup_location: pickupLocation,
      ret: dates.ret, price, deposit, notes,
      _business: business,
    },
    // Notepad starts empty on manual mode so the promoter can type it out.
    initial: {},
  },
  createdDraftTemplate: {
    customer, vehicle: `${vehicle} · ${plate}`,
    pickup: `${dates.pickup}${pickupLocation ? ` · ${pickupLocation}` : ""}`,
    ret: dates.ret,
    price, deposit, balance,
    source: channel.label || channel.kind,
    status: "Draft",
  },
  bookingCustomerName: customer,
  summaries: INTAKE_SUMMARIES,
});

const messenger = INTAKE({
  id: "messenger-inquiry",
  business: "Dumaguete Island Wheels",
  channel: {
    kind: "messenger", label: "Messenger",
    messages: [
      { from: "customer", text: "Hi po, good afternoon 🙏", at: 400 },
      { from: "customer", text: "Available po ba yung Toyota Vios nyo Aug 18 to Aug 20?", at: 1500 },
      { from: "staff",    text: "Hello po! Yes ma'am/sir, the Vios is available for those dates. May I have your name po for the reservation?", at: 3200 },
      { from: "customer", text: "Maria Santos po", at: 4600 },
      { from: "customer", text: "Pickup po sana Dumaguete Airport around 9am ng Aug 18", at: 5900 },
      { from: "staff",    text: "Noted po. Drop-off same airport also on Aug 20?", at: 7200 },
      { from: "customer", text: "Yes po same time 9am", at: 8300 },
      { from: "customer", text: "Magkano po yung daily rate?", at: 9400 },
      { from: "staff",    text: "₱1,700/day po for the Vios, so ₱3,400 for 2 days. Includes basic insurance + unlimited mileage sa Negros.", at: 11000 },
      { from: "customer", text: "Ok po. Pwede po mag-reserve? How much deposit?", at: 12500 },
      { from: "staff",    text: "₱1,000 po via GCash to hold the booking. Balance of ₱2,400 on pickup po.", at: 14300 },
      { from: "customer", text: "Sige po. My contact # is 0917-555-8842 in case need niyo tumawag.", at: 15900 },
      { from: "customer", text: "I have my driver's license din po ready for pickup.", at: 17100 },
    ],
  },
  customer: "Maria Santos", vehicle: "Toyota Vios", plate: "WSJ-2211",
  dates: { pickup: "Aug 18 · 09:00", ret: "Aug 20 · 09:00" },
  pickupLocation: "Dumaguete Airport",
  price: "₱1,700/day", deposit: "₱1,000", balance: "₱2,400",
  notes: "Contact: 0917-555-8842. Has driver's license. Airport pickup/drop-off same time.",
});

const whatsapp = INTAKE({
  id: "whatsapp-inquiry",
  business: "Cebu Wheels & Wander",
  channel: {
    kind: "whatsapp", label: "WhatsApp",
    messages: [
      { from: "customer", text: "Hi! Is the Innova free July 5 to July 8?", at: 400 },
      { from: "customer", text: "5 pax including driver. Family trip to Bantayan.", at: 1600 },
      { from: "staff",    text: "Good day sir! Yes the Innova is available July 5 - 8. May I have your full name po?", at: 3200 },
      { from: "customer", text: "David Cruz. From Manila, arriving Mactan on July 5 morning flight.", at: 4700 },
      { from: "customer", text: "Pickup Mactan Airport 10am please, return same place July 8 morning.", at: 6100 },
      { from: "staff",    text: "Noted po. Rate is ₱2,800/day - so ₱8,400 for 3 days. Includes fuel-full-out full-in and one driver.", at: 8000 },
      { from: "customer", text: "Do you allow self-drive? I have a Philippine + international license.", at: 9400 },
      { from: "staff",    text: "Yes po with license photo + valid ID. Deposit is ₱2,500 to hold, balance ₱5,900 on pickup.", at: 11200 },
      { from: "customer", text: "Perfect. Can I pay deposit via GCash tonight? My number is +63 917 224 6612.", at: 12800 },
      { from: "customer", text: "Also please note we need child seat kung meron kayo.", at: 14000 },
      { from: "staff",    text: "GCash ok po. Child seat available for ₱300 additional. I'll draft the booking now.", at: 15800 },
    ],
  },
  customer: "David Cruz", vehicle: "Toyota Innova", plate: "LFR-4098",
  dates: { pickup: "Jul 5 · 10:00", ret: "Jul 8 · 10:00" },
  pickupLocation: "Mactan Airport",
  price: "₱2,800/day", deposit: "₱2,500", balance: "₱5,900",
  notes: "5 pax family, self-drive with intl license. +63 917 224 6612. Child seat +₱300.",
});

const sms = INTAKE({
  id: "sms-inquiry",
  business: "Bacolod Ride Hub",
  channel: {
    kind: "sms", label: "SMS",
    messages: [
      { from: "customer", text: "Maam kailangan po ng motor Aug 2 to Aug 4. NMax po sana.", at: 400 },
      { from: "customer", text: "Pickup sa Bacolod City. Ako mismo ang driver.", at: 1500 },
      { from: "staff",    text: "Hi po, NMax available. Pwede po pangalan at cellphone number?", at: 3000 },
      { from: "customer", text: "Lea Padilla po, 0999-118-4402", at: 4300 },
      { from: "customer", text: "May license po ako. Magkano po yung 3 days?", at: 5600 },
      { from: "staff",    text: "₱650/day so ₱1,300 for 2 days po. Security deposit ₱500 via GCash.", at: 7100 },
      { from: "customer", text: "Ok po. Ano po yung GCash # nyo? Pwede po ba pickup ng 9am sa shop?", at: 8600 },
      { from: "staff",    text: "9am ok po. I'll send GCash # after confirming - may I take your address for the record?", at: 10200 },
      { from: "customer", text: "Sa Barangay Mansilingan po. Yung license ko is Philippine non-pro.", at: 11700 },
    ],
  },
  customer: "Lea Padilla", vehicle: "Yamaha NMax", plate: "155A-XY",
  dates: { pickup: "Aug 2 · 09:00", ret: "Aug 4 · 09:00" },
  pickupLocation: "Bacolod City shop",
  price: "₱650/day", deposit: "₱500", balance: "₱800",
  notes: "0999-118-4402. Brgy Mansilingan. PH non-pro license. Self-drive.",
});

const phone = INTAKE({
  id: "phone-call",
  business: "Iloilo Drive PH",
  channel: {
    kind: "phone", label: "Phone",
    messages: [
      { from: "system",   text: "Incoming call: 0932-778-1120", at: 200 },
      { from: "customer", text: "Hi, good morning. I'm looking for an automatic car August 5 to August 8.", at: 900 },
      { from: "staff",    text: "Good morning sir! Yes we have Honda City AT available. May I have your name?", at: 2200 },
      { from: "customer", text: "Michael Thompson. Foreign national - I have a US license plus international permit.", at: 3600 },
      { from: "staff",    text: "Okay sir Thompson, no problem with an international permit. Pickup where po?", at: 5100 },
      { from: "customer", text: "Iloilo Airport if possible, arriving August 5 at 9 AM.", at: 6500 },
      { from: "staff",    text: "Noted, we can meet you at Arrivals. Rate is ₱2,000/day - ₱6,000 for 3 days. Includes basic insurance.", at: 8300 },
      { from: "customer", text: "Great. Any additional fee for airport delivery?", at: 9700 },
      { from: "staff",    text: "No extra fee po for Iloilo Airport. Deposit is ₱2,000 to hold, balance ₱4,000 on pickup.", at: 11400 },
      { from: "customer", text: "Sounds good. Can you email me a written confirmation? mikethomp@example.com", at: 12800 },
      { from: "staff",    text: "Yes sir, I'll send the ConfirmPH link. May I have this same phone number for reminders?", at: 14400 },
      { from: "customer", text: "Yes, 0932-778-1120 is fine. I'll also send my passport photo to your Messenger.", at: 15800 },
    ],
  },
  customer: "Michael Thompson", vehicle: "Honda City AT", plate: "JAX-8842",
  dates: { pickup: "Aug 5 · 09:00", ret: "Aug 8 · 09:00" },
  pickupLocation: "Iloilo Airport",
  price: "₱2,000/day", deposit: "₱2,000", balance: "₱4,000",
  notes: "0932-778-1120. mikethomp@example.com. Foreign - US license + IDP. Airport delivery included.",
});

// Attach the shared text-intake scene photo to the three chat channels and
// the phone-call photo to the phone scenario.  Kept minimal (one frame each)
// so it grounds the moment without slowing down the actual demo.
messenger.intro = chatIntakeIntro("Messenger", messenger.business);
whatsapp.intro  = chatIntakeIntro("WhatsApp",  whatsapp.business);
sms.intro       = chatIntakeIntro("SMS",       sms.business);
phone.intro = [{
  img: SCENE.phoneCall,
  alt: `${phone.business}: staff on a call with a customer`,
  caption: `${phone.business}. The office landline rings: Gizelyn picks up while pulling ConfirmPH open on her computer.`,
}];

const walkin = {
  id: "walk-in",
  kind: "intake",
  business: "Isla Drive Car Rental",
  // No phone/channel: walk-in happens face-to-face at the reception desk.
  // Instead we play a scripted image intro that establishes the scene,
  // then drop the promoter straight into the desktop ConfirmPH Notepad.
  intro: [
    {
      img: "https://customer-assets-wrfwihn1.emergentagent.net/job_9c33b626-e523-4641-99a7-61794eb720c3/artifacts/yhs5m5ht_empty_office.png",
      alt: "Empty Isla Drive reception counter",
      caption: "Isla Drive's Dumaguete branch, 2:04 PM. Reception is quiet: vehicles waiting outside, key board fully stocked.",
    },
    {
      img: "https://customer-assets-wrfwihn1.emergentagent.net/job_9c33b626-e523-4641-99a7-61794eb720c3/artifacts/ligj9tst_employee_working_in_the_office.png",
      alt: "Gizelyn at her desk, checking today's schedule",
      caption: "Gizelyn, the guest service associate, opens ConfirmPH on her office computer to check today's schedule.",
    },
    {
      img: "https://customer-assets-wrfwihn1.emergentagent.net/job_9c33b626-e523-4641-99a7-61794eb720c3/artifacts/594hhjm9_walk_in_customer.png",
      alt: "Customer walks into the office",
      caption: "2:14 PM: Carlo walks in asking for a Honda Click he can use until tomorrow. No prior chat, no reservation, just walked in.",
    },
    {
      img: "https://customer-assets-wrfwihn1.emergentagent.net/job_9c33b626-e523-4641-99a7-61794eb720c3/artifacts/9xprv0j9_employee_customer_conversation_behind_desk_confirmph_notepad.png",
      alt: "Gizelyn captures Carlo's request in the ConfirmPH Notepad",
      caption: "While they talk, Gizelyn opens the ConfirmPH Notepad and types Carlo's details as he speaks: no forms to fight, just the note.",
    },
    {
      img: "https://customer-assets-wrfwihn1.emergentagent.net/job_9c33b626-e523-4641-99a7-61794eb720c3/artifacts/d7woq5gt_employee_customer_conversation_behind_desk_business_dashboard.png",
      alt: "Gizelyn converts the note into a booking on the Business Dashboard",
      caption: "Details captured. She switches to the Business Dashboard to convert the note into a real booking while Carlo is still at the counter.",
    },
  ],
  home: ["notepad", "dashboard"],
  entry: "notepad",
  notepad: {
    placeholder: {
      customer: "Carlo Dela Cruz", contact: "walk_in",
      vehicle: "Honda Click",
      pickup: "Today · 14:15", pickup_location: "Isla Drive counter",
      ret: "Tomorrow · 14:00",
      price: "₱450/day", deposit: "₱200",
      notes: "0917-334-6621. LTO license + PhilID captured. Solo rider Metro Manila only. Cash payment.",
      _business: "Isla Drive Car Rental",
    },
    initial: { _source: "walk_in" },
  },
  createdDraftTemplate: {
    customer: "Carlo Dela Cruz", vehicle: "Honda Click · 771-BCD",
    pickup: "Today · 14:15 · Isla Drive counter",
    ret: "Tomorrow · 14:00",
    price: "₱450/day", deposit: "₱200", balance: "₱250",
    source: "Walk-in",
    status: "Draft",
  },
  bookingCustomerName: "Carlo Dela Cruz",
  summaries: {
    drafted: {
      title: "Walk-in booking recorded from the counter",
      what:  "The customer's request was captured in the Notepad, promoted into a draft, and turned into a booking: all before they left the desk. No paper slip, no lost details.",
      related: ["upcoming-pickup", "deposit-balance", "messenger-inquiry"],
      relatedIntro: "The same intake works for online channels too: try it from Messenger.",
    },
    confirmed: INTAKE_SUMMARIES.confirmed,
  },
};

/* --------------- OPERATIONS: dashboard-first workflows --------------- */

// Double booking: start on Dashboard, an existing booking is present, an
// incoming draft overlaps, conflict warning is triggered.
const doubleBooking = {
  id: "double-booking",
  kind: "dashboard-conflict",
  business: "Dumaguete Island Wheels",
  home: ["dashboard", "notepad"],
  entry: "dashboard",
  vehicles: [
    { plate: "WSJ-2211", model: "Toyota Vios", status: "Assigned Jun 12–14" },
    { plate: "JHK-3312", model: "Toyota Corolla", status: "Available" },
  ],
  bookings: [
    { id: "b-1", customer: "Maria Santos", vehicle: "Toyota Vios · WSJ-2211",
      pickup: "Jun 12 · 09:00", ret: "Jun 14 · 09:00",
      source: "Messenger", status: "Confirmed" },
  ],
  incomingDraft: {
    customer: "John Miller", vehicle: "Toyota Vios · WSJ-2211",
    pickup: "Jun 13 · 09:00", ret: "Jun 15 · 09:00",
    price: "₱1,700/day", source: "Phone", status: "Draft",
  },
  conflict: {
    warn: "Toyota Vios is already assigned to another booking during part of these dates.",
    existing: "Maria Santos · Jun 12–14",
    incoming: "John Miller · Jun 13–15",
    options: ["Choose another vehicle", "Change dates", "Contact customer"],
  },
  reassignedVehicle: { plate: "JHK-3312", model: "Toyota Corolla" },
  summaries: {
    resolved_conflict: {
      title: "Overlap warning resolved",
      what:  "ConfirmPH spotted the conflict as soon as the second booking was created. You reassigned the incoming customer to another vehicle without disturbing the existing booking.",
      related: ["tight-buffer", "date-change", "vehicle-breakdown"],
    },
  },
};

const tightBuffer = {
  id: "tight-buffer",
  kind: "dashboard-conflict",
  business: "Cebu Wheels & Wander",
  home: ["dashboard"],
  entry: "dashboard",
  vehicles: [
    { plate: "LMN-901", model: "Mitsubishi Mirage", status: "Ongoing · returns 10:00" },
  ],
  bookings: [
    { id: "a", customer: "Anna Reyes",  vehicle: "Mitsubishi Mirage · LMN-901",
      pickup: "Yesterday · 10:00", ret: "Today · 10:00",
      source: "Messenger", status: "Ongoing" },
    { id: "b", customer: "Ben Salazar", vehicle: "Mitsubishi Mirage · LMN-901",
      pickup: "Today · 11:00", ret: "Sat · 11:00",
      source: "Phone", status: "Pending" },
  ],
  bannerWarn: {
    warn: "Only 1 hour between bookings on Mitsubishi Mirage · LMN-901.",
    affected: [{ customer: "Ben Salazar", when: "Today · 11:00" }],
    options: ["Move Booking B to 12:30", "Assign a different vehicle", "Prepare rapid turnover"],
  },
  summaries: {
    resolved_conflict: {
      title: "Tight buffer identified before pickup day",
      what:  "The one-hour gap between the returning rental and the next pickup was flagged in advance, giving staff a chance to plan the turnover or shift the next pickup.",
      related: ["double-booking", "late-return", "upcoming-pickup"],
    },
  },
};

const breakdown = {
  id: "vehicle-breakdown",
  kind: "vehicle-issue",
  business: "Traveler Car Rental",
  home: ["dashboard"],
  entry: "dashboard",
  vehicles: [
    { plate: "PYR-5502", model: "Kia Rio", status: "Available",
      canMarkMaintenance: true,
      // Real damage photo, shown in the Vehicle detail as the evidence
      // Gizelyn sees when the returning renter reports the accident.
      incidentPhoto: {
        url: SCENE.damagedCar,
        caption: "Reported this morning by the returning renter: front-left impact. Photo taken at the shop.",
      },
    },
    { plate: "KDQ-7710", model: "Toyota Montero",  status: "Available" },
  ],
  bookings: [
    { id: "vb-1", customer: "Anna Reyes", vehicle: "Kia Rio · PYR-5502",
      pickup: "Tomorrow · 09:00", ret: "Sat · 18:00",
      source: "Messenger", status: "Confirmed" },
  ],
  onMarkMaintenance: {
    warn: "Anna Reyes' booking tomorrow at 09:00 depends on Kia Rio · PYR-5502.",
    affected: [{ customer: "Anna Reyes", when: "Tomorrow · 09:00" }],
    options: ["Assign replacement vehicle", "Contact customer", "Offer alternate dates"],
    replacement: { plate: "KDQ-7710", model: "Toyota Montero" },
  },
  summaries: {
    replacement_assigned: {
      title: "Replacement vehicle assigned before the customer arrived",
      what:  "The Kia Rio was pulled from the fleet as soon as the damage was reported, the affected upcoming booking was surfaced, and a Toyota Montero was assigned to Anna Reyes without her needing to chase you.",
      related: ["damage-incident", "maintenance", "replacement-vehicle"],
    },
  },
};

const lateReturn = {
  id: "late-return",
  kind: "vehicle-issue",
  business: "Iloilo Drive PH",
  home: ["dashboard"],
  entry: "dashboard",
  vehicles: [
    { plate: "MOP-9911", model: "Suzuki Ertiga", status: "Overdue return · was due 08:00" },
  ],
  bookings: [
    { id: "lr-cur",  customer: "Rafael Uy",       vehicle: "Suzuki Ertiga · MOP-9911",
      pickup: "Yesterday · 09:00", ret: "Today · 08:00 (overdue)",
      source: "Messenger", status: "Ongoing · overdue" },
    { id: "lr-next", customer: "Zara Hernandez",  vehicle: "Suzuki Ertiga · MOP-9911",
      pickup: "Today · 12:00", ret: "Fri · 18:00",
      source: "WhatsApp",  status: "At risk" },
  ],
  bannerWarn: {
    warn: "Rafael Uy hasn't returned Suzuki Ertiga. Next booking begins at 12:00.",
    affected: [{ customer: "Zara Hernandez", when: "Today · 12:00" }],
    options: ["Message Rafael", "Call Rafael", "Notify Zara of possible delay",
                "Assign replacement vehicle"],
  },
  summaries: {
    resolved_conflict: {
      title: "Late-return risk surfaced against the next pickup",
      what:  "The overdue rental was tied to the next booking automatically, so staff could reach out before the pickup time and offer options, not scramble on the day.",
      related: ["vehicle-breakdown", "replacement-vehicle", "follow-up"],
    },
  },
};

const replacement = {
  id: "replacement-vehicle",
  kind: "vehicle-issue",
  business: "Bacolod Ride Hub",
  home: ["dashboard"],
  entry: "dashboard",
  vehicles: [
    { plate: "TRX-8871", model: "Toyota Rush",   status: "Repair" },
    { plate: "MEC-4491", model: "Toyota Avanza", status: "Available Sat 08:00 onwards" },
  ],
  bookings: [
    { id: "rp-1", customer: "Kim Bautista", vehicle: "Toyota Rush · TRX-8871",
      pickup: "Sat · 10:00", ret: "Sun · 18:00",
      source: "Messenger", status: "Vehicle unavailable" },
  ],
  bannerWarn: {
    warn: "Toyota Rush · TRX-8871 is under repair.",
    affected: [{ customer: "Kim Bautista", when: "Sat · 10:00" }],
    options: ["Assign replacement vehicle", "Contact customer"],
    replacement: { plate: "MEC-4491", model: "Toyota Avanza" },
  },
  summaries: {
    replacement_assigned: {
      title: "Booking updated to the replacement vehicle",
      what:  "Kim's booking record now points at the Toyota Avanza. The original request, dates and price are still on the same record: nothing was recreated.",
      related: ["vehicle-breakdown", "damage-incident", "maintenance"],
    },
  },
};

const depositBalance = {
  id: "deposit-balance",
  kind: "financial",
  business: "Dumaguete Island Wheels",
  home: ["dashboard"],
  entry: "dashboard",
  vehicles: [
    { plate: "WSJ-2211", model: "Toyota Vios", status: "Assigned" },
  ],
  bookings: [
    { id: "db-1", customer: "Maria Santos", vehicle: "Toyota Vios · WSJ-2211",
      pickup: "Aug 18 · 09:00", ret: "Aug 20 · 09:00",
      source: "Messenger", status: "Confirmed",
      subtotal: "₱3,400", deposit: "₱1,000", balance: "₱2,400" },
  ],
  finalOutcome: "Paid: recorded",
  summaries: {
    balance_settled: {
      title: "Payment recorded on the booking",
      what:  "The ₱4,000 balance was logged directly against the booking, so the booking record, not a chat screenshot: is the source of truth for how much was paid.",
      related: ["upcoming-pickup", "verified-review", "messenger-inquiry"],
    },
  },
};

/* -----------------------------------------------------------------
 * PUBLIC-SURFACE DEMOS
 * ----------------------------------------------------------------- */

// Small stock rental photos (Unsplash): safe, generic, free-to-use.
const PHOTO = {
  vios:    "https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?w=800",
  innova:  "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=800",
  fortuner:"https://images.unsplash.com/photo-1550355291-bbee04a92027?w=800",
  nmax:    "https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?w=800",
};

const businessPage = {
  id: "business-page",
  kind: "public-page",
  business: "Isla Drive Car Rental",
  home: ["business-page"],
  entry: "business-page",
  defaultDevice: "desktop",
  publicBusiness: {
    name: "Isla Drive Car Rental",
    tagline: "Family-run self-drive rentals · Dumaguete and Cebu",
    location: "Dumaguete City, Negros Oriental",
    rating: 4.9,
    review_count: 138,
    deposit_required: 1000,
    suggested: {
      start: "2026-03-18T09:00",
      end:   "2026-03-20T09:00",
    },
    terms: "Valid driver's licence + one valid ID required at pickup. Full-tank-out, full-tank-in. Metro Negros travel only unless approved in writing.",
    vehicles: [
      { id: "vios",    name: "Toyota Vios",    transmission: "Automatic", seats: 5, rate: 1700, photo: PHOTO.vios },
      { id: "innova",  name: "Toyota Innova",  transmission: "Automatic", seats: 7, rate: 2800, photo: PHOTO.innova },
      { id: "fortuner",name: "Toyota Fortuner",transmission: "Automatic", seats: 7, rate: 4500, photo: PHOTO.fortuner },
      { id: "nmax",    name: "Yamaha NMax",    transmission: "Automatic", seats: 2, rate: 650,  photo: PHOTO.nmax  },
    ],
  },
  // After the guest submits, we drop the promoter into the office computer
  // (dashboard) so they can show the inquiry landing in Booking Intake.
  onSubmitSwitchTo: "dashboard",
  summaries: {
    submitted: {
      title: "Customer request landed in Booking Intake",
      what:  "The customer picked a vehicle, chose dates, and hit send: the request now sits inside your ConfirmPH Booking Intake instead of a shared Messenger inbox.",
      related: ["messenger-inquiry", "public-website", "awaiting-confirmation"],
    },
  },
};

const website = {
  id: "public-website",
  kind: "public-site",
  business: "Isla Drive Car Rental",
  home: ["website"],
  entry: "website",
  defaultDevice: "desktop",
  websiteData: {
    name: "Isla Drive Car Rental",
    location: "Dumaguete · Cebu · Bohol",
    hero_title: "Drive the islands your way.",
    hero_sub: "Airport pickup, 24/7 support, and a fleet built for Philippine roads. Request online, confirm clearly.",
    rating: 4.9,
    review_count: 138,
    phone: "+63 917 555 8842",
    email: "hello@isladrive.ph",
    about: "Isla Drive has served visitors and locals in Negros Oriental since 2018. We maintain every vehicle in-house, pick you up at the airport, and stay on call for the whole trip.\n\nWe're a small family team: Gizelyn manages the desk, Rey handles maintenance, and Kuya Boy drives our airport transfers.",
    policies: [
      { title: "Deposit",       body: "₱1,000 to hold a booking, refundable within 24h of return if no damage/violations." },
      { title: "Cancellation",  body: "Free cancellation up to 48h before pickup. 50% fee inside 24h. No-show forfeits deposit." },
      { title: "Fuel",          body: "Full-tank-out, full-tank-in. We fuel-check with you at both ends." },
      { title: "Damage",        body: "Basic insurance included. Excess starts at ₱3,000 depending on vehicle class." },
      { title: "Travel area",   body: "Metro Negros included. Cross-island travel by written approval only." },
    ],
    faq: [
      { q: "Do you accept foreign licences?", a: "Yes: a valid home-country licence plus an International Driving Permit is accepted." },
      { q: "Can I request airport pickup?",   a: "Yes. Dumaguete Airport pickup is included; Sibulan and Mactan on request." },
      { q: "Is a credit card required?",      a: "No: GCash, bank transfer, and cash on pickup are all accepted." },
      { q: "How fast is confirmation?",       a: "Requests are usually confirmed within one hour during business hours (7am–9pm)." },
    ],
    reviews: [
      { name: "Maria S.",   stars: 5, body: "Very clean Vios and Gizelyn answered every question. Will book again.", when: "2 weeks ago" },
      { name: "David C.",   stars: 5, body: "Airport pickup was on time, Innova ran perfectly for our Bantayan trip.", when: "1 month ago" },
      { name: "Michael T.", stars: 4, body: "Smooth process. Only wish they had a Ford Ranger available.",           when: "1 month ago" },
    ],
    vehicles: [
      { id: "vios",    name: "Toyota Vios",    transmission: "Automatic", seats: 5, rate: 1700, photo: PHOTO.vios },
      { id: "innova",  name: "Toyota Innova",  transmission: "Automatic", seats: 7, rate: 2800, photo: PHOTO.innova },
      { id: "fortuner",name: "Toyota Fortuner",transmission: "Automatic", seats: 7, rate: 4500, photo: PHOTO.fortuner },
      { id: "nmax",    name: "Yamaha NMax",    transmission: "Automatic", seats: 2, rate: 650,  photo: PHOTO.nmax  },
    ],
  },
  summaries: {
    explored: {
      title: "Your rental website in one place",
      what:  "Home, Fleet, Booking, About, Policies, FAQ, Reviews and Contact are all powered by your ConfirmPH data: a bookable rental website without a separate hosting bill.",
      related: ["business-page", "messenger-inquiry", "verified-review"],
    },
  },
};

export const WORKFLOW_SCENARIOS = {
  [messenger.id]:      messenger,
  [whatsapp.id]:       whatsapp,
  [sms.id]:            sms,
  [phone.id]:          phone,
  [walkin.id]:         walkin,
  [doubleBooking.id]:  doubleBooking,
  [tightBuffer.id]:    tightBuffer,
  [breakdown.id]:      breakdown,
  [lateReturn.id]:     lateReturn,
  [replacement.id]:    replacement,
  [depositBalance.id]: depositBalance,
  [businessPage.id]:   businessPage,
  [website.id]:        website,
  // Custom-kind scenarios (interactive real-app screens).  See ./custom.js.
  ...customWorkflows,
};

export function getWorkflow(scenarioId) {
  return WORKFLOW_SCENARIOS[scenarioId] || null;
}
